import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, Tag, FileText, Calculator, Wrench, CheckCircle2, ExternalLink, ClipboardList, Clock, Handshake } from "lucide-react";
import { api, formatApiErrorDetail, priceLabel } from "@/lib/api";
import { brandStyle } from "@/lib/brand";
import { saveUnlock } from "@/lib/unlock";
import { CommentField } from "@/components/CommentField";
import { EstimateLines } from "@/components/EstimateLines";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const inputCls = "bg-slate-950 border-slate-800 focus:border-brand text-slate-100";

export const WALLS = {
  lower_price: { icon: Tag, title: "Unlock a lower price", blurb: (d) => `${d} has a lower price on this vehicle for shoppers who leave their details.`, cta: "Show me the lower price" },
  carfax: { icon: FileText, title: "View the CARFAX report", blurb: (d) => `${d} shares the full CARFAX vehicle history with shoppers who leave their details.`, cta: "Unlock the CARFAX report" },
  carfax_request: { icon: FileText, title: "Request the CARFAX report", blurb: (d) => `${d} will send you the full CARFAX vehicle history for this vehicle — leave your details and they'll get it to you.`, cta: "Send me the CARFAX report", request: true },
  estimate: { icon: Wrench, title: "See the repair estimate", blurb: (d) => `${d} shares exactly what this As-Is unit needs to pass state inspection — and what it costs — with shoppers who leave their details.`, cta: "Unlock the repair estimate" },
  payment: { icon: Calculator, title: "Open the payment calculator", blurb: (d) => `Estimate your monthly payment on ${d}'s calculator — just leave your details first.`, cta: "Open the calculator" },
  recon: { icon: ClipboardList, title: "See the reconditioning report", blurb: (d) => `${d} shares what the shop did to this vehicle before it hit the lot — with shoppers who leave their details.`, cta: "Show me the recon report" },
  time_on_lot: { icon: Clock, title: "How long has it been here?", blurb: (d) => `${d} tells shoppers who leave their details exactly how long this vehicle has been on the lot.`, cta: "Show me the time on lot" },
  acquisition: { icon: Handshake, title: "How did they get this vehicle?", blurb: (d) => `Trade-in, lease return, auction… ${d} shares where this vehicle came from with shoppers who leave their details.`, cta: "Show me where it came from" },
};

const UNLOCK_TEXT = { lower_price: "a lower price", carfax: "the CARFAX report", estimate: "the As-Is repair estimate", payment: "the payment calculator",
  recon: "the reconditioning report", time_on_lot: "the time on lot", acquisition: "how the dealer acquired the vehicle" };

const EMPTY = { customer_name: "", phone: "", email: "", comment: "" };

// One contact wall for all three gates. After one submit everything at this dealership is unlocked for an hour.
export default function LeadWallDialog({ open, onOpenChange, kind, vehicle, dealer, onUnlocked }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [sp] = useSearchParams();
  const wall = WALLS[kind] || WALLS.lower_price;
  const Icon = wall.icon;

  const close = (o) => {
    onOpenChange(o);
    if (!o) setTimeout(() => { setResult(null); setForm(EMPTY); }, 300);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.phone.trim() && !form.email.trim()) {
      toast.error("Add a mobile number or email so the dealership can reach you");
      return;
    }
    setBusy(true);
    try {
      const { data: lead } = await api.post("/public/leads", {
        vehicle_id: vehicle.id,
        dealer_id: dealer.id,
        source: `wall_${kind}`,
        unlock: kind === "carfax_request" ? "carfax" : kind,
        customer_name: form.customer_name,
        phone: form.phone,
        email: form.email,
        comment: form.comment,
        tour: sp.get("tour") || "",
        tour_session: sp.get("ts") || "",
        message: kind === "carfax_request"
          ? `Requested the CARFAX report on the ${vehicle.year} ${vehicle.make} ${vehicle.model} (Stock ${vehicle.stock_number || "n/a"}) — please send it to them.`
          : `Unlocked ${UNLOCK_TEXT[kind] || kind} on the ${vehicle.year} ${vehicle.make} ${vehicle.model} (Stock ${vehicle.stock_number || "n/a"}).`,
      });
      saveUnlock(dealer.id, lead);
      const { data } = await api.get(`/public/vehicles/${vehicle.id}/unlocked`, { params: { token: lead.unlock_token } });
      setResult(data);
      onUnlocked?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const first = form.customer_name.trim().split(" ")[0] || "there";

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="lead-wall-dialog" style={brandStyle(dealer?.accent_color)} className="bg-[#0F111A] border-[#2A2E3D] text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-extrabold flex items-center gap-2">
            <Icon className="h-5 w-5 text-brand" /> {wall.title}
          </DialogTitle>
          <DialogDescription className="text-slate-400">{wall.blurb(dealer?.name || "The dealership")}</DialogDescription>
        </DialogHeader>

        {result ? (
          <div data-testid="lead-wall-success" className="py-4 space-y-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-8 w-8 text-emerald-400 shrink-0" />
              <div>
                <div className="font-heading text-lg font-bold">{wall.request ? `Got it, ${first}!` : `Unlocked, ${first}!`}</div>
                <div className="text-xs text-slate-400">{wall.request ? `${dealer?.name || "The dealership"} will send the CARFAX report to your ${form.phone.trim() ? "phone" : "email"} shortly.` : `Everything on this page stays open for you for the next hour — even on other ${dealer?.name} vehicles.`}</div>
              </div>
            </div>
            {kind === "carfax_request" && (
              <p className="text-sm text-slate-300 rounded-xl border border-[#3777BC]/40 bg-[#3777BC]/10 p-4" data-testid="lead-wall-carfax-requested">
                Your request is on its way to {dealer?.name || "the dealership"}. Meanwhile, everything else on this page is open for you for the next hour.
              </p>
            )}
            {kind === "lower_price" && result.lower_price != null && (
              <div className="rounded-2xl border border-brand/40 bg-brand/10 p-5 text-center" data-testid="lead-wall-lower-price">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono-tech">Your price</div>
                <div className="font-heading text-4xl font-black text-brand mt-1">{priceLabel(result.lower_price)}</div>
                {result.savings > 0 && (
                  <div className="text-sm text-slate-300 mt-1">
                    <span className="line-through text-slate-500 mr-2">{priceLabel(result.list_price)}</span>
                    You save <b className="text-emerald-400">{priceLabel(result.savings)}</b>
                  </div>
                )}
              </div>
            )}
            {kind === "carfax" && (
              result.carfax_url ? (
                <Button asChild data-testid="lead-wall-open-carfax" className="w-full h-11 bg-[#3777BC] hover:bg-[#3777BC]/85 text-white font-bold">
                  <a href={result.carfax_url} target="_blank" rel="noopener"><FileText className="mr-2 h-4 w-4" /> Open CARFAX report <ExternalLink className="ml-2 h-3.5 w-3.5" /></a>
                </Button>
              ) : (
                <p className="text-sm text-slate-400" data-testid="lead-wall-no-carfax">A CARFAX report isn't available for this vehicle — {dealer?.name} will follow up with the history.</p>
              )
            )}
            {kind === "estimate" && (
              result.estimate_url ? (
                <Button asChild data-testid="lead-wall-open-estimate" className="w-full h-11 bg-amber-500 hover:bg-amber-400 text-black font-bold">
                  <a href={result.estimate_url} target="_blank" rel="noopener"><Wrench className="mr-2 h-4 w-4" /> Open the repair estimate <ExternalLink className="ml-2 h-3.5 w-3.5" /></a>
                </Button>
              ) : result.repair_items?.length ? (
                <EstimateLines items={result.repair_items} total={result.repair_estimate_total} testId="lead-wall-estimate-lines" />
              ) : (
                <p className="text-sm text-slate-400" data-testid="lead-wall-no-estimate">The estimate isn't posted yet — {dealer?.name} will follow up with the details.</p>
              )
            )}
            {kind === "payment" && (
              result.payment_url ? (
                <a href={result.payment_url} target="_blank" rel="noreferrer" className="block">
                  <Button data-testid="lead-wall-open-payment" className="w-full h-11 bg-brand hover:bg-brand/90 text-white font-bold">
                    <Calculator className="mr-2 h-4 w-4" /> Open payment calculator <ExternalLink className="ml-2 h-3.5 w-3.5" />
                  </Button>
                </a>
              ) : (
                <p className="text-sm text-slate-400" data-testid="lead-wall-no-payment">{dealer?.name} will reach out with payment options.</p>
              )
            )}
            {kind === "recon" && (
              result.insider?.recon ? (
                <div className="rounded-2xl border border-brand/40 bg-brand/10 p-4 space-y-2" data-testid="lead-wall-recon">
                  <div className="text-sm font-semibold">{result.insider.recon.label}</div>
                  {result.insider.recon.notes && <p className="text-sm text-slate-300 whitespace-pre-line">{result.insider.recon.notes}</p>}
                  {result.insider.recon.file_url && (
                    <Button asChild data-testid="lead-wall-open-recon" className="w-full h-11 bg-brand hover:bg-brand/90 text-white font-bold">
                      <a href={result.insider.recon.file_url} target="_blank" rel="noopener"><ClipboardList className="mr-2 h-4 w-4" /> Open the recon report <ExternalLink className="ml-2 h-3.5 w-3.5" /></a>
                    </Button>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-400" data-testid="lead-wall-no-recon">The report isn't posted yet — {dealer?.name} will follow up with the details.</p>
              )
            )}
            {kind === "time_on_lot" && result.insider?.time_on_lot && (
              <div className="rounded-2xl border border-brand/40 bg-brand/10 p-5 text-center" data-testid="lead-wall-time-on-lot">
                <div className="font-heading text-3xl font-black text-brand">{result.insider.time_on_lot.label}</div>
                {result.insider.time_on_lot.since && <div className="text-xs text-slate-400 mt-1">Arrived {new Date(`${result.insider.time_on_lot.since}T12:00:00`).toLocaleDateString(undefined, { month: "long", day: "numeric" })}</div>}
              </div>
            )}
            {kind === "acquisition" && result.insider?.acquisition && (
              <div className="rounded-2xl border border-brand/40 bg-brand/10 p-4" data-testid="lead-wall-acquisition">
                <div className="text-sm font-semibold">{result.insider.acquisition.label}</div>
                {result.insider.acquisition.notes && <p className="text-sm text-slate-300 mt-1 whitespace-pre-line">{result.insider.acquisition.notes}</p>}
              </div>
            )}
            <Button variant="ghost" onClick={() => close(false)} data-testid="lead-wall-done-btn" className="w-full text-slate-400 hover:text-white">Back to the vehicle</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="lead-wall-form">
            <div>
              <Label className="text-slate-300 mb-1.5 block">Your name</Label>
              <Input required data-testid="wall-name-input" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className={inputCls} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300 mb-1.5 block">Mobile</Label>
                <Input type="tel" data-testid="wall-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 555-0100" className={inputCls} />
              </div>
              <div>
                <Label className="text-slate-300 mb-1.5 block">Email</Label>
                <Input type="email" data-testid="wall-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" className={inputCls} />
              </div>
            </div>
            <CommentField value={form.comment} onChange={(comment) => setForm({ ...form, comment })} testId="wall-comment" />
            <Button type="submit" disabled={busy || !form.customer_name.trim() || (!form.phone.trim() && !form.email.trim())} data-testid="wall-submit-btn" className="w-full h-11 bg-brand hover:bg-brand/90 font-bold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="mr-2 h-4 w-4" /> {wall.cta}</>}
            </Button>
            <p className="text-[11px] text-slate-500 text-center">Mobile or email — at least one. Shared only with {dealer?.name || "the dealership"}.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
